import { FieldTestChecklistItem } from '../data-models/field-test-checklist-item.model';
import { FieldTestFile } from '../data-models/field-test-file.model';
import { CustomFieldTestFileRequest } from '../request-models/custom-field-test-file-request.model';
import { CustomFieldTestRequest } from '../request-models/custom-field-test-request.model';
import { FieldTestRequest } from '../request-models/field-test-request.model';
import { isRecord } from './is-record';
import { idString, nullableNumber, nullableString, omitUndefined } from './wire-value-utils';

/**
 * Reads a field test checklist: `undefined` when the hub didn't send it, `null` when it sent `null`.
 * The checklist is always sent and saved whole, so an item's unanswered `passed` reads as `false`.
 */
export function toChecklist(value: unknown): FieldTestChecklistItem[] | null | undefined {
  if (value === null) {
    return null;
  }
  if (!Array.isArray(value)) {
    return undefined;
  }
  return value.filter(isRecord).map(item => {
    const checklistItem: FieldTestChecklistItem = { item: nullableString(item['item']) ?? '', passed: item['passed'] === true };
    const notes = nullableString(item['notes']);
    if (notes) {
      checklistItem.notes = notes;
    }
    return checklistItem;
  });
}

/** Reads a submission's or template's files (read-only; missing reads as no files). */
export function toFieldTestFiles(value: unknown): FieldTestFile[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(isRecord).map(file => ({
    id: idString(file['id']) || undefined,
    name: nullableString(file['fileName']) ?? '',
    type: nullableString(file['fileType']) ?? '',
    filePath: nullableString(file['filePath']) ?? '',
    size: nullableNumber(file['fileSize']) ?? undefined,
  }));
}

/** The field test create/update body: only the allowlisted keys that are set (`null` clears a field). */
export function toFieldTestRequest(request: FieldTestRequest): FieldTestRequest {
  return omitUndefined({
    title: request.title,
    testType: request.testType,
    aircraftId: request.aircraftId,
    aircraftName: request.aircraftName,
    pilotId: request.pilotId,
    pilotName: request.pilotName,
    testDate: request.testDate,
    location: request.location,
    checklist: request.checklist?.map(copyChecklistItem),
    notes: request.notes,
  });
}

/** The custom field test (submission or template) body: only the allowlisted keys that are set. */
export function toCustomFieldTestRequest(request: CustomFieldTestRequest): CustomFieldTestRequest {
  return omitUndefined({
    title: request.title,
    testType: request.testType,
    category: request.category,
    description: request.description,
    applicableAircraft: request.applicableAircraft,
    frequency: request.frequency,
    requiresSignoff: request.requiresSignoff,
    files: request.files?.map(toCustomFieldTestFileRequest),
  });
}

/** A file attachment body with only its allowlisted keys. */
export function toCustomFieldTestFileRequest(file: CustomFieldTestFileRequest): CustomFieldTestFileRequest {
  return omitUndefined({ fileName: file.fileName, fileType: file.fileType, filePath: file.filePath, fileSize: file.fileSize });
}

/**
 * The value to send for an optional text field on create: a blank entry is not sent at all.
 * (On edit use `clearableFieldValue`, which sends `null` for a field the user emptied.)
 */
export function blankToUndefined(value: string | null | undefined): string | undefined {
  return value?.trim() ? value : undefined;
}

function copyChecklistItem(item: FieldTestChecklistItem): FieldTestChecklistItem {
  return omitUndefined({ item: item.item, passed: item.passed, notes: item.notes });
}
